import { useRouter } from 'next/router';
import { buildPageHref } from '../../util/PaginationUtil';

/**
 * The "Load More" control for every listing page.
 *
 * This replaces the `<div onClick={() => setPage(page + 1)}>` each listing used
 * to carry. That div was invisible to search engines — there was no `href` to
 * queue — which is why no page past the first in any series could be crawled.
 *
 * The anchor is the source of truth and the click handler is an enhancement on
 * top of it:
 *
 *   * a crawler, or a visitor without JavaScript, follows the href and gets the
 *     next page as a normal navigation;
 *   * everyone else keeps the existing append-in-place behaviour, with the URL
 *     updated so the position is shareable and survives a refresh.
 *
 * Deliberately a plain `<a>` rather than `next/link`: a prefetching Link would
 * pull down the next page's payload on hover for a navigation that, for most
 * visitors, never happens — the click is intercepted.
 */
const PaginationLink = ({
  page,
  onLoadMore,
  children,
  className = 'show-more',
  id,
  // Whether the click should also push the new page into the address bar.
  //
  // Listings that replace the visible results page by page set this (the
  // default): the URL then matches what is on screen, and a reload brings the
  // visitor back to the same place.
  //
  // Listings that keep growing one long list — the Partner Directory — pass
  // false. There the URL would claim "page 3" while the screen shows pages one
  // through three, and reloading would silently drop the first two. The href is
  // still a real link for crawlers; only the visitor's address bar is left
  // alone.
  updateUrl = true,
  // Defaults to "next" because that is what these controls almost always are.
  // Show All jumps to the end of the series and Show Less returns to its start,
  // so they say "last" and "first" instead — telling a crawler the link skips
  // rather than advances.
  rel = 'next',
}) => {
  const router = useRouter();

  // The path as the router sees it — no locale prefix. Next keeps the locale
  // in `router.locale` and strips it from `asPath`, and puts it back itself on
  // every `push`/`replace`. So this is what the click handler below needs.
  const path = buildPageHref(router?.asPath, page);

  // The href, which is a plain attribute and gets no such help. Without the
  // prefix the markup on /de/resources/blog advertised
  // `/resources/blog?page=2` — the English page two. Clicking it was fine,
  // because the router re-applied the locale, but a crawler reading the source
  // followed it straight out of the translation and never reached page two in
  // German, French, Spanish, Italian, Korean or Chinese.
  const { locale, defaultLocale } = router || {};
  const href =
    locale && locale !== defaultLocale ? `/${locale}${path}` : path;

  const handleClick = (event) => {
    // Hand these back to the browser untouched. A visitor who ⌘-clicks or
    // middle-clicks is asking for a real navigation in a new tab, which is
    // impossible today because the control is a div — and it is also the
    // quickest way for QA to confirm the href is genuinely there.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    onLoadMore(page);

    if (!updateUrl) {
      return;
    }

    // `replace`, not `push`: the visitor is still on the same listing, so Back
    // should return them to the page they arrived from rather than unwinding
    // one Load More click at a time. `scroll: false` keeps the viewport where
    // it is — results are appended below, nothing moved.
    //
    // `path`, not `href`: the router adds the locale itself, so handing it the
    // prefixed form would produce `/de/de/resources/blog`.
    router.replace(path, undefined, { shallow: true, scroll: false });
  };

  return (
    <a
      href={href}
      rel={rel}
      className={`pagination-link ${className}`}
      id={id}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export default PaginationLink;
